import Papa from "papaparse";
import { Node, Beam } from "@/types/nnstructural2D";
import { SUPPORT_VISUAL_CONFIGS } from "../types/support";

// =====================================================
// CSV Import/Export Utilities
// =====================================================

export interface CSVImportResult<T> {
  success: boolean;
  data: T[];
  errors: string[];
  warnings: string[];
  preview: T[];
  csv: string;
}

export interface CSVExportOptions {
  filename?: string;
  includeHeaders?: boolean;
  format?: "csv" | "tsv";
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// =====================================================
// Node CSV Operations
// =====================================================

export interface NodeCSVData {
  id: string;
  x: number;
  y: number;
  z: number;
}

/**
 * Validate node CSV data
 */
export const validateNodeCSVData = (data: any[]): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (data.length === 0) {
    errors.push("No data found in CSV file");
    return { isValid: false, errors, warnings };
  }

  data.forEach((row, index) => {
    const rowNumber = index + 1;

    // Check for required fields
    if (!row.id && row.id !== 0) {
      errors.push(`Row ${rowNumber}: Missing node ID`);
    }

    if (typeof row.x !== "number" || isNaN(row.x)) {
      errors.push(`Row ${rowNumber}: Invalid X coordinate`);
    }

    if (typeof row.y !== "number" || isNaN(row.y)) {
      errors.push(`Row ${rowNumber}: Invalid Y coordinate`);
    }

    if (typeof row.z !== "number" || isNaN(row.z)) {
      errors.push(`Row ${rowNumber}: Invalid Z coordinate`);
    }

    // Check for duplicate IDs
    const duplicateIndex = data.findIndex(
      (r, i) => i < index && r.id === row.id
    );
    if (duplicateIndex !== -1) {
      warnings.push(
        `Row ${rowNumber}: Duplicate node ID '${row.id}' (also found at row ${duplicateIndex + 1})`
      );
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Transform raw CSV data to Node objects
 */
export const transformNodeCSVData = (rawData: any[]): Node[] => {
  return rawData.map((node, index) => ({
    id: node.id?.toString() || (index + 1).toString(),
    x: typeof node.x === "number" ? node.x : parseFloat(node.x) || 0,
    y: typeof node.y === "number" ? node.y : parseFloat(node.y) || 0,
    z: typeof node.z === "number" ? node.z : parseFloat(node.z) || 0,
    isSelected: false,
    color: "#000000",
  }));
};

/**
 * Import nodes from CSV file
 */
export const importNodesFromCSV = (
  file: File
): Promise<CSVImportResult<Node>> => {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rawData = result.data as any[];
        const validation = validateNodeCSVData(rawData);

        if (!validation.isValid) {
          resolve({
            success: false,
            data: [],
            errors: validation.errors,
            warnings: validation.warnings,
            preview: [],
            csv: "",
          });
          return;
        }

        const transformedData = transformNodeCSVData(rawData);
        const csv = Papa.unparse(
          transformedData.map((n) => ({ id: n.id, x: n.x, y: n.y, z: n.z }))
        );

        resolve({
          success: true,
          data: transformedData,
          errors: validation.errors,
          warnings: validation.warnings,
          preview: transformedData.slice(0, 5),
          csv,
        });
      },
      error: (error) => {
        resolve({
          success: false,
          data: [],
          errors: [`CSV parsing error: ${error.message}`],
          warnings: [],
          preview: [],
          csv: "",
        });
      },
    });
  });
};

/**
 * Export nodes to CSV
 */
export const exportNodesToCSV = (
  nodes: Node[],
  options: CSVExportOptions = {}
): void => {
  const {
    filename = "nodes.csv",
    includeHeaders = true,
    format = "csv",
  } = options;

  const csvData = nodes.map((node) => ({
    id: node.id,
    x: node.x,
    y: node.y,
    z: node.z,
  }));

  const csv = Papa.unparse(csvData, {
    header: includeHeaders,
    delimiter: format === "tsv" ? "\t" : ",",
  });

  const blob = new Blob([csv], {
    type:
      format === "tsv"
        ? "text/tab-separated-values"
        : "text/csv;charset=utf-8;",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// =====================================================
// Beam CSV Operations
// =====================================================

export interface BeamCSVData {
  id: string;
  startNodeId: string;
  endNodeId: string;
  material: string;
  label?: string;
}

/**
 * Validate beam CSV data
 */
export const validateBeamCSVData = (
  data: any[],
  existingNodeIds: string[] = []
): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (data.length === 0) {
    errors.push("No data found in CSV file");
    return { isValid: false, errors, warnings };
  }

  data.forEach((row, index) => {
    const rowNumber = index + 1;

    // Check for required fields
    if (!row.startNodeId && row.startNodeId !== 0) {
      errors.push(`Row ${rowNumber}: Missing start node ID`);
    }

    if (!row.endNodeId && row.endNodeId !== 0) {
      errors.push(`Row ${rowNumber}: Missing end node ID`);
    }

    // Check for self-referencing beams
    if (row.startNodeId === row.endNodeId) {
      errors.push(
        `Row ${rowNumber}: Start and end node IDs cannot be the same`
      );
    }

    // Check for missing nodes if we have existing nodes to check against
    if (existingNodeIds.length > 0) {
      if (!existingNodeIds.includes(row.startNodeId?.toString())) {
        warnings.push(
          `Row ${rowNumber}: Start node '${row.startNodeId}' does not exist`
        );
      }
      if (!existingNodeIds.includes(row.endNodeId?.toString())) {
        warnings.push(
          `Row ${rowNumber}: End node '${row.endNodeId}' does not exist`
        );
      }
    }

    // Check for duplicate IDs
    const duplicateIndex = data.findIndex(
      (r, i) => i < index && r.id === row.id
    );
    if (duplicateIndex !== -1) {
      warnings.push(
        `Row ${rowNumber}: Duplicate beam ID '${row.id}' (also found at row ${duplicateIndex + 1})`
      );
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Transform raw CSV data to Beam objects
 */
export const transformBeamCSVData = (rawData: any[]): Beam[] => {
  return rawData.map((beam, index) => ({
    id: beam.id?.toString() || (index + 1).toString(),
    startNodeId:
      beam.startNodeId?.toString() ||
      beam.S_Node?.toString() ||
      beam.startNode?.toString() ||
      "",
    endNodeId:
      beam.endNodeId?.toString() ||
      beam.E_Node?.toString() ||
      beam.endNode?.toString() ||
      "",
    material: beam.material || beam.Material || "Steel",
    isSelected: false,
    color: "#4dabf7",
  }));
};

/**
 * Import beams from CSV file
 */
export const importBeamsFromCSV = (
  file: File,
  existingNodeIds: string[] = []
): Promise<CSVImportResult<Beam>> => {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rawData = result.data as any[];
        const validation = validateBeamCSVData(rawData, existingNodeIds);

        if (!validation.isValid) {
          resolve({
            success: false,
            data: [],
            errors: validation.errors,
            warnings: validation.warnings,
            preview: [],
            csv: "",
          });
          return;
        }

        const transformedData = transformBeamCSVData(rawData);
        const csv = Papa.unparse(
          transformedData.map((b) => ({
            id: b.id,
            startNodeId: b.startNodeId,
            endNodeId: b.endNodeId,
            material: b.material,
            P_ID: b.P_ID || "XP3",
          }))
        );

        resolve({
          success: true,
          data: transformedData,
          errors: validation.errors,
          warnings: validation.warnings,
          preview: transformedData.slice(0, 5),
          csv,
        });
      },
      error: (error) => {
        resolve({
          success: false,
          data: [],
          errors: [`CSV parsing error: ${error.message}`],
          warnings: [],
          preview: [],
          csv: "",
        });
      },
    });
  });
};

/**
 * Export beams to CSV
 */
export const exportBeamsToCSV = (
  beams: Beam[],
  options: CSVExportOptions = {}
): void => {
  const {
    filename = "beams.csv",
    includeHeaders = true,
    format = "csv",
  } = options;

  const csvData = beams.map((beam) => ({
    id: beam.id,
    startNodeId: beam.startNodeId,
    endNodeId: beam.endNodeId,
    material: beam.material || "Steel",
    P_ID: beam.P_ID || "XP3",
  }));

  const csv = Papa.unparse(csvData, {
    header: includeHeaders,
    delimiter: format === "tsv" ? "\t" : ",",
  });

  const blob = new Blob([csv], {
    type:
      format === "tsv"
        ? "text/tab-separated-values"
        : "text/csv;charset=utf-8;",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// =====================================================
// Excel File Processing
// =====================================================

/**
 * Process Excel file using Electron API
 */
export const processExcelFile = async (
  file: File
): Promise<CSVImportResult<any>> => {
  try {
    // Check if electronAPI is available
    if (
      typeof window !== "undefined" &&
      (window as any).electronAPI &&
      (window as any).electronAPI.processExcel
    ) {
      const result = await (window as any).electronAPI.processExcel(
        (file as any).path
      );

      if (result.success) {
        const rawData = result.preview as any[];
        const csv = result.csv;

        return {
          success: true,
          data: rawData,
          errors: [],
          warnings: [],
          preview: rawData.slice(0, 5),
          csv,
        };
      } else {
        return {
          success: false,
          data: [],
          errors: [`Excel processing error: ${result.error}`],
          warnings: [],
          preview: [],
          csv: "",
        };
      }
    } else {
      return {
        success: false,
        data: [],
        errors: [
          "Excel processing is not available in this environment. Please use CSV files instead.",
        ],
        warnings: [],
        preview: [],
        csv: "",
      };
    }
  } catch (error) {
    return {
      success: false,
      data: [],
      errors: [`An error occurred: ${error}`],
      warnings: [],
      preview: [],
      csv: "",
    };
  }
};

// =====================================================
// Clipboard Operations
// =====================================================

/**
 * Copy data to clipboard as TSV
 */
export const copyToClipboard = async (
  data: any[],
  headers?: string[]
): Promise<boolean> => {
  try {
    let tsv = "";

    if (headers && headers.length > 0) {
      tsv = headers.join("\t") + "\n";
    }

    tsv += data
      .map((row) => {
        if (Array.isArray(row)) {
          return row.join("\t");
        } else if (typeof row === "object") {
          return Object.values(row).join("\t");
        } else {
          return String(row);
        }
      })
      .join("\n");

    await navigator.clipboard.writeText(tsv);
    return true;
  } catch (error) {
    console.error("Failed to copy to clipboard:", error);
    return false;
  }
};

/**
 * Copy nodes to clipboard
 */
export const copyNodesToClipboard = async (nodes: Node[]): Promise<boolean> => {
  const tsvData = nodes.map((n) => `${n.id}\t${n.x}\t${n.y}\t${n.z}`);
  return copyToClipboard(tsvData, ["ID", "X", "Y", "Z"]);
};

/**
 * Copy beams to clipboard
 */
export const copyBeamsToClipboard = async (beams: Beam[]): Promise<boolean> => {
  const tsvData = beams.map(
    (b) => `${b.id}\t${b.startNodeId}\t${b.endNodeId}\t${b.material || "Steel"}`
  );
  return copyToClipboard(tsvData, ["ID", "Start Node", "End Node", "Material"]);
};

// =====================================================
// Utility Functions
// =====================================================

/**
 * Generate unique filename with timestamp
 */
export const generateFilename = (
  baseName: string,
  extension: string = "csv"
): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return `${baseName}_${timestamp}.${extension}`;
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Validate file type
 */
export const validateFileType = (
  file: File,
  allowedTypes: string[]
): boolean => {
  const extension = file.name.split(".").pop()?.toLowerCase();
  return extension ? allowedTypes.includes(extension) : false;
};

/**
 * Create error message for file validation
 */
export const createFileValidationError = (
  file: File,
  allowedTypes: string[]
): string => {
  const extension = file.name.split(".").pop()?.toLowerCase();
  return `Unsupported file type: ${extension}. Please select a ${allowedTypes.join(" or ")} file.`;
};

// =====================================================
// Section Properties CSV Operations
// =====================================================

/**
 * Export section properties to CSV
 */
export const exportSectionPropertiesToCSV = (
  properties: any[],
  options: CSVExportOptions = {}
): void => {
  const {
    filename = generateFilename("section-properties"),
    includeHeaders = true,
  } = options;

  const csvData = properties.map((prop) => {
    const baseData: any = {
      P_ID: prop.P_ID || "",
      Material: prop.material || "None",
      SectionType: prop.sectionType || "",
      Designation: prop.sectionProperties?.designation || "",
      BeamIDs: (prop.beamIds || []).join(";"),
    };

    // ✅ CRITICAL: Export ALL dimensional properties based on section type
    const sectionProps = prop.sectionProperties || {};

    // Add all possible dimensional fields (will be empty for non-matching types)
    const dimensionalFields = [
      "A",
      "B",
      "t",
      "r1",
      "r2",
      "Cxx",
      "Cyy",
      "size",
      "exx",
      "eyy",
      "h",
      "b",
      "tw",
      "tf",
      "D",
      "r3",
      "tanAlpha",
      "h1",
      "h2",
      "b1",
      "C",
      "g",
      "g1Min",
      "maxRivet",
    ];

    dimensionalFields.forEach((field) => {
      baseData[field] = sectionProps[field] ?? "";
    });

    baseData.CreatedAt = prop.createdAt || "";
    baseData.UpdatedAt = prop.updatedAt || "";

    return baseData;
  });

  const csv = Papa.unparse(csvData, {
    header: includeHeaders,
    skipEmptyLines: true,
  });

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Import section properties from CSV
 */
/**
 * Import section properties from CSV
 */
export const importSectionPropertiesFromCSV = async (
  file: File
): Promise<CSVImportResult<any>> => {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      transformHeader: (header: string) => {
        // Normalize header names
        const normalized = header.trim().toLowerCase();
        if (
          normalized === "p_id" ||
          normalized === "propertyid" ||
          normalized === "property id"
        )
          return "P_ID";
        if (normalized === "material") return "Material";
        if (normalized === "sectiontype" || normalized === "section type")
          return "SectionType";
        if (normalized === "designation") return "Designation";
        if (
          normalized === "beamids" ||
          normalized === "beam ids" ||
          normalized === "beams"
        )
          return "BeamIDs";
        return header;
      },
      complete: (results) => {
        try {
          const errors: string[] = [];
          const warnings: string[] = [];

          if (results.errors.length > 0) {
            results.errors.forEach((err) => {
              errors.push(`Parse error at row ${err.row}: ${err.message}`);
            });
          }

          if (!results.data || results.data.length === 0) {
            resolve({
              success: false,
              data: [],
              errors: ["No data found in CSV file"],
              warnings: [],
              preview: [],
              csv: "",
            });
            return;
          }

          // Transform CSV data to PropertyDefinition format
          const properties = results.data.map((row: any, index: number) => {
            const beamIdsStr = row.BeamIDs || "";
            const beamIds = beamIdsStr
              ? beamIdsStr
                  .split(/[;,]/)
                  .map((id: string) => id.trim())
                  .filter(Boolean)
              : [];

            // ✅ Parse section properties based on section type
            let sectionProperties: any = {
              designation: row.Designation || "",
            };

            // ✅ Add dimensional data based on section type
            const sectionType = row.SectionType;

            if (sectionType === "Equal Angle") {
              sectionProperties = {
                designation: row.Designation || "",
                A: parseFloat(row.A) || 0,
                B: parseFloat(row.B) || 0,
                t: parseFloat(row.t) || 0,
                r1: parseFloat(row.r1) || 0,
                r2: parseFloat(row.r2) || 0,
                Cxx: parseFloat(row.Cxx) || 0,
                Cyy: parseFloat(row.Cyy) || 0,
                size: row.size || "",
                exx: parseFloat(row.exx) || 0,
                eyy: parseFloat(row.eyy) || 0,
              };
            } else if (sectionType === "Bulb Angle") {
              sectionProperties = {
                designation: row.Designation || "",
                h: parseFloat(row.h) || 0,
                b: parseFloat(row.b) || 0,
                tw: parseFloat(row.tw) || 0,
                tf: parseFloat(row.tf) || 0,
                D: parseFloat(row.D) || 0,
                r1: parseFloat(row.r1) || 0,
                r2: parseFloat(row.r2) || 0,
                r3: parseFloat(row.r3) || 0,
                Cxx: parseFloat(row.Cxx) || 0,
                Cyy: parseFloat(row.Cyy) || 0,
                tanAlpha: parseFloat(row.tanAlpha) || 0,
                size: row.size || "",
                exx: parseFloat(row.exx) || 0,
                eyy: parseFloat(row.eyy) || 0,
              };
            } else if (sectionType === "I-Beam") {
              sectionProperties = {
                designation: row.Designation || "",
                h: parseFloat(row.h) || 0,
                b: parseFloat(row.b) || 0,
                tf: parseFloat(row.tf) || 0,
                tw: parseFloat(row.tw) || 0,
                r1: parseFloat(row.r1) || 0,
                r2: parseFloat(row.r2) || 0,
                D: parseFloat(row.D) || 0,
                h1: parseFloat(row.h1) || 0,
                h2: parseFloat(row.h2) || 0,
                b1: parseFloat(row.b1) || 0,
                C: parseFloat(row.C) || 0,
                g: parseFloat(row.g) || 0,
                g1Min: parseFloat(row.g1Min) || 0,
                maxRivet: parseFloat(row.maxRivet) || 0,
              };
            }
            // ✅ ADD THIS FOR TEE BAR
            else if (sectionType === "Tee Bar") {
              sectionProperties = {
                designation: row.Designation || "",
                h: parseFloat(row.h) || 0,
                b: parseFloat(row.b) || 0,
                tw: parseFloat(row.tw) || 0,
                tf: parseFloat(row.tf) || 0,
                D: parseFloat(row.D) || 0,
                r1: parseFloat(row.r1) || 0,
                r2: parseFloat(row.r2) || 0,
                Cxx: parseFloat(row.Cxx) || 0,
                Cyy: parseFloat(row.Cyy) || 0,
                size: row.size || "",
                exx: parseFloat(row.exx) || 0,
                eyy: parseFloat(row.eyy) || 0,
              };
            }
            // ✅ ADD GENERIC FALLBACK FOR ALL OTHER TYPES
            else {
              // Generic fallback: parse all available fields
              sectionProperties = {
                designation: row.Designation || "",
                // Parse all dimensional fields generically
                ...(row.A && { A: parseFloat(row.A) || 0 }),
                ...(row.B && { B: parseFloat(row.B) || 0 }),
                ...(row.t && { t: parseFloat(row.t) || 0 }),
                ...(row.h && { h: parseFloat(row.h) || 0 }),
                ...(row.b && { b: parseFloat(row.b) || 0 }),
                ...(row.tw && { tw: parseFloat(row.tw) || 0 }),
                ...(row.tf && { tf: parseFloat(row.tf) || 0 }),
                ...(row.r1 && { r1: parseFloat(row.r1) || 0 }),
                ...(row.r2 && { r2: parseFloat(row.r2) || 0 }),
                ...(row.r3 && { r3: parseFloat(row.r3) || 0 }),
                ...(row.D && { D: parseFloat(row.D) || 0 }),
                ...(row.Cxx && { Cxx: parseFloat(row.Cxx) || 0 }),
                ...(row.Cyy && { Cyy: parseFloat(row.Cyy) || 0 }),
                ...(row.tanAlpha && {
                  tanAlpha: parseFloat(row.tanAlpha) || 0,
                }),
                ...(row.h1 && { h1: parseFloat(row.h1) || 0 }),
                ...(row.h2 && { h2: parseFloat(row.h2) || 0 }),
                ...(row.b1 && { b1: parseFloat(row.b1) || 0 }),
                ...(row.C && { C: parseFloat(row.C) || 0 }),
                ...(row.g && { g: parseFloat(row.g) || 0 }),
                ...(row.g1Min && { g1Min: parseFloat(row.g1Min) || 0 }),
                ...(row.maxRivet && {
                  maxRivet: parseFloat(row.maxRivet) || 0,
                }),
                ...(row.size && { size: row.size }),
                ...(row.exx && { exx: parseFloat(row.exx) || 0 }),
                ...(row.eyy && { eyy: parseFloat(row.eyy) || 0 }),
              };
            }

            // Add other section types as needed...

            return {
              P_ID: row.P_ID?.toString() || `P${index + 1}`,
              beamIds,
              material: row.Material || "None",
              sectionType: row.SectionType || undefined,
              sectionProperties,
              createdAt: row.CreatedAt || new Date().toISOString(),
              updatedAt: row.UpdatedAt || new Date().toISOString(),
            };
          });

          // Validate required fields
          properties.forEach((prop, index) => {
            if (!prop.P_ID || prop.P_ID.trim() === "") {
              errors.push(`Row ${index + 1}: Property ID is required`);
            }
          });

          // Check for duplicate P_IDs
          const pidMap = new Map();
          properties.forEach((prop, index) => {
            if (pidMap.has(prop.P_ID)) {
              warnings.push(
                `Row ${index + 1}: Duplicate P_ID '${prop.P_ID}' (also found at row ${pidMap.get(prop.P_ID)! + 1})`
              );
            } else {
              pidMap.set(prop.P_ID, index);
            }
          });

          const csvString = Papa.unparse(results.data);

          resolve({
            success: errors.length === 0,
            data: properties,
            errors,
            warnings,
            preview: properties.slice(0, 5),
            csv: csvString,
          });
        } catch (error) {
          resolve({
            success: false,
            data: [],
            errors: [`An error occurred while processing the file: ${error}`],
            warnings: [],
            preview: [],
            csv: "",
          });
        }
      },
      error: (error) => {
        resolve({
          success: false,
          data: [],
          errors: [`Failed to parse CSV file: ${error.message}`],
          warnings: [],
          preview: [],
          csv: "",
        });
      },
    });
  });
};

/**
 * Copy section properties to clipboard
 */
export const copySectionPropertiesToClipboard = async (
  properties: any[]
): Promise<boolean> => {
  const tsvData = properties.map(
    (prop) =>
      `${prop.P_ID}\t${prop.material || "None"}\t${prop.sectionType || ""}\t${prop.sectionProperties?.designation || ""}\t${(prop.beamIds || []).join(";")}`
  );
  return copyToClipboard(tsvData, [
    "P_ID",
    "Material",
    "Section Type",
    "Designation",
    "Beam IDs",
  ]);
};

// =====================================================
// Load Case CSV Operations
// =====================================================

/**
 * Import load cases from CSV
 */
export const importLoadCasesFromCSV = (
  file: File
): Promise<CSVImportResult<any>> => {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const errors: string[] = [];
          const warnings: string[] = [];

          const loadCases = results.data.map((row: any, index: number) => {
            const loadCase: any = {
              id: row.id?.toString() || `loadcase-${index + 1}`,
              name: row.name || row.Name || `Load Case ${index + 1}`,
              category: row.category || row.Category || "general",
              type: row.type || row.Type || "distributed1",
            };

            // Parse load values based on type
            if (loadCase.type === "distributed1") {
              loadCase.values = {
                dx:
                  typeof row.dx === "number" ? row.dx : parseFloat(row.dx) || 0,
                dy:
                  typeof row.dy === "number" ? row.dy : parseFloat(row.dy) || 0,
                dz:
                  typeof row.dz === "number" ? row.dz : parseFloat(row.dz) || 0,
              };
            } else if (loadCase.type === "distributed2") {
              loadCase.values = {
                dxStart:
                  typeof row.dxStart === "number"
                    ? row.dxStart
                    : parseFloat(row.dxStart) || 0,
                dyStart:
                  typeof row.dyStart === "number"
                    ? row.dyStart
                    : parseFloat(row.dyStart) || 0,
                dzStart:
                  typeof row.dzStart === "number"
                    ? row.dzStart
                    : parseFloat(row.dzStart) || 0,
                dxEnd:
                  typeof row.dxEnd === "number"
                    ? row.dxEnd
                    : parseFloat(row.dxEnd) || 0,
                dyEnd:
                  typeof row.dyEnd === "number"
                    ? row.dyEnd
                    : parseFloat(row.dyEnd) || 0,
                dzEnd:
                  typeof row.dzEnd === "number"
                    ? row.dzEnd
                    : parseFloat(row.dzEnd) || 0,
              };
            } else if (loadCase.type === "concentrated") {
              loadCase.values = {
                fx:
                  typeof row.fx === "number" ? row.fx : parseFloat(row.fx) || 0,
                fy:
                  typeof row.fy === "number" ? row.fy : parseFloat(row.fy) || 0,
                fz:
                  typeof row.fz === "number" ? row.fz : parseFloat(row.fz) || 0,
                referenceNode:
                  row.referenceNode || row.Reference_Node || "S_Node",
                percentageFromNode:
                  typeof row.percentageFromNode === "number"
                    ? row.percentageFromNode
                    : parseFloat(row.percentageFromNode) || 0,
              };
            }

            return loadCase;
          });

          const csvString = Papa.unparse(results.data);

          resolve({
            success: errors.length === 0,
            data: loadCases,
            errors,
            warnings,
            preview: loadCases.slice(0, 5),
            csv: csvString,
          });
        } catch (error) {
          resolve({
            success: false,
            data: [],
            errors: [`An error occurred while processing the file: ${error}`],
            warnings: [],
            preview: [],
            csv: "",
          });
        }
      },
      error: (error) => {
        resolve({
          success: false,
          data: [],
          errors: [`Failed to parse CSV file: ${error.message}`],
          warnings: [],
          preview: [],
          csv: "",
        });
      },
    });
  });
};

/**
 * Export load cases to CSV
 */
export const exportLoadCasesToCSV = (
  loadCases: any[],
  options: CSVExportOptions = {}
): void => {
  const { filename = generateFilename("loadcases"), includeHeaders = true } =
    options;

  const csvData = loadCases.map((loadCase) => {
    const baseData: any = {
      id: loadCase.id,
      name: loadCase.name,
      category: loadCase.category || "general",
      type: loadCase.type || "distributed1",
    };

    // Add type-specific values
    if (loadCase.values) {
      Object.assign(baseData, loadCase.values);
    }

    return baseData;
  });

  const csv = Papa.unparse(csvData, { header: includeHeaders });

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Copy load cases to clipboard
 */
export const copyLoadCasesToClipboard = async (
  loadCases: any[]
): Promise<boolean> => {
  const tsvData = loadCases.map((loadCase) => {
    const values = loadCase.values || {};
    const valuesStr = Object.entries(values)
      .map(([k, v]) => `${k}:${v}`)
      .join(";");
    return `${loadCase.id}\t${loadCase.name}\t${loadCase.category || "general"}\t${loadCase.type || "distributed1"}\t${valuesStr}`;
  });
  return copyToClipboard(tsvData, ["ID", "Name", "Category", "Type", "Values"]);
};

// =====================================================
// Support CSV Operations
// =====================================================

export interface SupportCSVData {
  id: string;
  nodeId: string;
  type: "fixed" | "pinned" | "roller";
  position: "below" | "above" | "left" | "right" | "front" | "back";
  Fx: number;
  Fy: number;
  Fz: number;
  Rx: number;
  Ry: number;
  Rz: number;
}

/**
 * Validate support CSV data
 */
export const validateSupportCSVData = (data: any[]): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (data.length === 0) {
    errors.push("No data found in CSV file");
    return { isValid: false, errors, warnings };
  }

  const validTypes = ["fixed", "pinned", "roller"];
  const validPositions = ["below", "above", "left", "right", "front", "back"];

  data.forEach((row, index) => {
    const rowNumber = index + 1;

    // Allow empty nodeId - support will be associated when node is created
    if (row.nodeId && row.nodeId !== "") {
      warnings.push(
        `Row ${rowNumber}: Node ID '${row.nodeId}' - will be validated during import`
      );
    }

    // CHECK: Required type field
    if (!row.type || !validTypes.includes(row.type.toLowerCase())) {
      errors.push(
        `Row ${rowNumber}: Invalid support type '${row.type}'. Must be: fixed, pinned, or roller`
      );
    }

    // CHECK: Required position field
    if (!row.position || !validPositions.includes(row.position.toLowerCase())) {
      errors.push(
        `Row ${rowNumber}: Invalid position '${row.position}'. Must be: below, above, left, right, front, or back`
      );
    }

    // Check DOF values (optional)
    const dofFields = ["Fx", "Fy", "Fz", "Rx", "Ry", "Rz"];
    dofFields.forEach((field) => {
      if (
        row[field] !== undefined &&
        row[field] !== "" &&
        typeof row[field] !== "number" &&
        isNaN(parseFloat(row[field]))
      ) {
        warnings.push(
          `Row ${rowNumber}: Invalid ${field} value '${row[field]}', defaulting to 0`
        );
      }
    });
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Transform raw CSV data to Support objects
 */
export const transformSupportCSVData = (rawData: any[]): any[] => {
  return rawData.map((support, index) => {
    let nodeId = "";
    if (
      support.nodeId !== undefined &&
      support.nodeId !== null &&
      support.nodeId !== ""
    ) {
      nodeId = support.nodeId.toString().trim();
    }

    const type = (support.type || "fixed").toString().toLowerCase() as
      | "fixed"
      | "pinned"
      | "roller";
    const position = (support.position || "below").toString().toLowerCase();

    const defaultColor = SUPPORT_VISUAL_CONFIGS[type]?.color || "#00ff00";

    return {
      id:
        support.id?.toString() ||
        `support_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      nodeId: nodeId,
      type: type,
      position: position,
      dof: {
        Fx:
          typeof support.Fx === "number"
            ? support.Fx
            : parseFloat(support.Fx) || 0,
        Fy:
          typeof support.Fy === "number"
            ? support.Fy
            : parseFloat(support.Fy) || 0,
        Fz:
          typeof support.Fz === "number"
            ? support.Fz
            : parseFloat(support.Fz) || 0,
        Rx:
          typeof support.Rx === "number"
            ? support.Rx
            : parseFloat(support.Rx) || 0,
        Ry:
          typeof support.Ry === "number"
            ? support.Ry
            : parseFloat(support.Ry) || 0,
        Rz:
          typeof support.Rz === "number"
            ? support.Rz
            : parseFloat(support.Rz) || 0,
      },
      color: support.color || defaultColor,
      opacity: support.opacity ?? 0.6,
      size: support.size ?? 1.0,
      offset: support.offset ?? 0.1,
    };
  });
};

/**
 * Import supports from CSV
 */
export const importSupportsFromCSV = (
  file: File
): Promise<CSVImportResult<any>> => {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const errors: string[] = [];
          const warnings: string[] = [];

          if (!results.data || results.data.length === 0) {
            resolve({
              success: false,
              data: [],
              errors: ["No data found in CSV file"],
              warnings: [],
              preview: [],
              csv: "",
            });
            return;
          }

          const supports = transformSupportCSVData(results.data);

          const validTypes = ["fixed", "pinned", "roller"];
          const validPositions = [
            "below",
            "above",
            "left",
            "right",
            "front",
            "back",
          ];

          supports.forEach((support, index) => {
            const rowNumber = index + 1;

            if (!validTypes.includes(support.type)) {
              errors.push(
                `Row ${rowNumber}: Invalid support type '${support.type}'`
              );
            }

            if (!validPositions.includes(support.position)) {
              errors.push(
                `Row ${rowNumber}: Invalid position '${support.position}'`
              );
            }

            // Warn about empty nodeId
            if (!support.nodeId || support.nodeId === "") {
              warnings.push(
                `Row ${rowNumber}: No node ID - support will be associated when node is created`
              );
            }
          });

          const csvString = Papa.unparse(results.data);

          resolve({
            success: errors.length === 0,
            data: supports,
            errors: errors,
            warnings: warnings,
            preview: supports.slice(0, 5),
            csv: csvString,
          });
        } catch (error) {
          resolve({
            success: false,
            data: [],
            errors: [
              `An error occurred while processing the file: ${error instanceof Error ? error.message : String(error)}`,
            ],
            warnings: [],
            preview: [],
            csv: "",
          });
        }
      },
      error: (error) => {
        resolve({
          success: false,
          data: [],
          errors: [`Failed to parse CSV file: ${error.message}`],
          warnings: [],
          preview: [],
          csv: "",
        });
      },
    });
  });
};

/**
 * Export supports to CSV
 */
export const exportSupportsToCSV = (
  supports: any[],
  options: CSVExportOptions = {}
): void => {
  const { filename = generateFilename("supports"), includeHeaders = true } =
    options;

  const csvData = supports.map((support) => ({
    id: support.id,
    nodeId: support.nodeId || "",
    type: support.type,
    position: support.position,
    Fx: support.dof?.Fx ?? 0,
    Fy: support.dof?.Fy ?? 0,
    Fz: support.dof?.Fz ?? 0,
    Rx: support.dof?.Rx ?? 0,
    Ry: support.dof?.Ry ?? 0,
    Rz: support.dof?.Rz ?? 0,
  }));

  const csv = Papa.unparse(csvData, { header: includeHeaders });

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Copy supports to clipboard
 */
export const copySupportsToClipboard = async (
  supports: any[]
): Promise<boolean> => {
  const tsvData = supports.map(
    (support) =>
      `${support.id}\t${support.nodeId || ""}\t${support.type}\t${support.position}\t${support.dof?.Fx ?? 0}\t${support.dof?.Fy ?? 0}\t${support.dof?.Fz ?? 0}\t${support.dof?.Rx ?? 0}\t${support.dof?.Ry ?? 0}\t${support.dof?.Rz ?? 0}`
  );
  return copyToClipboard(tsvData, [
    "ID",
    "Node ID",
    "Type",
    "Position",
    "Fx",
    "Fy",
    "Fz",
    "Rx",
    "Ry",
    "Rz",
  ]);
};

// =====================================================
// Beam Load CSV Operations
// =====================================================

export interface BeamLoadCSVData {
  Load_Name: string;
  Beam_ID: string;
  Type: string;
  dx?: number;
  dy?: number;
  dz?: number;
  dxStart?: number;
  dyStart?: number;
  dzStart?: number;
  dxEnd?: number;
  dyEnd?: number;
  dzEnd?: number;
  fx?: number;
  fy?: number;
  fz?: number;
  referenceNode?: string;
  percentageFromNode?: number;
  torque?: number;
  torqueDirection?: string;
}

/**
 * Import beam loads from CSV
 */
export const importBeamLoadsFromCSV = (
  file: File
): Promise<CSVImportResult<any>> => {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const loadMap = new Map<string, any>();
          const errors: string[] = [];
          const warnings: string[] = [];

          results.data.forEach((row: any, index: number) => {
            const rowNumber = index + 1;
            const loadName = row.LoadName || row.Load_Name || row.loadName || `Load_${rowNumber}`;
            const beamId = row.BeamID || row.Beam_ID || row.beamId;
            const type = row.Type || row.type || "distributed_uniform";

            if (!loadName || loadName.trim() === "") {
              warnings.push(`Row ${rowNumber}: Missing load name, using default`);
            }

            if (!loadMap.has(loadName)) {
              loadMap.set(loadName, {
                id: Date.now().toString() + Math.random(),
                label: loadName,
                type,
                beamIds: [],
                // Distributed uniform
                dx: parseFloat(row.dx) || 0,
                dy: parseFloat(row.dy) || 0,
                dz: parseFloat(row.dz) || 0,
                // Distributed variable
                dxLeft: parseFloat(row.dxStart || row.dxLeft) || 0,
                dyLeft: parseFloat(row.dyStart || row.dyLeft) || 0,
                dzLeft: parseFloat(row.dzStart || row.dzLeft) || 0,
                dxRight: parseFloat(row.dxEnd || row.dxRight) || 0,
                dyRight: parseFloat(row.dyEnd || row.dyRight) || 0,
                dzRight: parseFloat(row.dzEnd || row.dzRight) || 0,
                // Concentrated
                fx: parseFloat(row.fx) || 0,
                fy: parseFloat(row.fy) || 0,
                fz: parseFloat(row.fz) || 0,
                referenceNode: row.referenceNode || row.Reference_Node || "S_Node",
                percentageFromNode: parseFloat(row.percentageFromNode) || 0,
                // Twisted
                torque: parseFloat(row.torque) || 0,
                torqueDirection: row.torqueDirection || "clockwise",
              });
            }

            const loadData = loadMap.get(loadName);
            if (beamId !== null && beamId !== undefined && beamId !== "") {
              loadData.beamIds.push(beamId.toString());
            }
          });

          const csvString = Papa.unparse(results.data);

          resolve({
            success: errors.length === 0,
            data: Array.from(loadMap.values()),
            errors,
            warnings,
            preview: Array.from(loadMap.values()).slice(0, 5),
            csv: csvString,
          });
        } catch (error) {
          resolve({
            success: false,
            data: [],
            errors: [`Failed to process beam loads: ${error}`],
            warnings: [],
            preview: [],
            csv: "",
          });
        }
      },
      error: (error) => {
        resolve({
          success: false,
          data: [],
          errors: [`CSV parsing failed: ${error.message}`],
          warnings: [],
          preview: [],
          csv: "",
        });
      },
    });
  });
};

/**
 * Export beam loads to CSV
 */
export const exportBeamLoadsToCSV = (
  beamLoads: any[],
  options: CSVExportOptions = {}
): void => {
  const { filename = generateFilename("beam-loadcases"), includeHeaders = true } = options;

  const csvData: BeamLoadCSVData[] = [];

  beamLoads.forEach((load: any) => {
    const beamIds = Array.isArray(load.beamIds) ? load.beamIds : [];

    if (beamIds.length === 0) {
      // No beams assigned - export one row with empty Beam_ID
      csvData.push({
        Load_Name: load.label || "Unnamed",
        Beam_ID: "",
        Type: load.type || "distributed_uniform",
        dx: load.dx ?? "",
        dy: load.dy ?? "",
        dz: load.dz ?? "",
        dxStart: load.dxLeft ?? "",
        dyStart: load.dyLeft ?? "",
        dzStart: load.dzLeft ?? "",
        dxEnd: load.dxRight ?? "",
        dyEnd: load.dyRight ?? "",
        dzEnd: load.dzRight ?? "",
        fx: load.fx ?? "",
        fy: load.fy ?? "",
        fz: load.fz ?? "",
        referenceNode: load.referenceNode ?? "",
        percentageFromNode: load.percentageFromNode ?? "",
        torque: load.torque ?? "",
        torqueDirection: load.torqueDirection ?? "",
      } as any);
    } else {
      beamIds.forEach((beamId: string) => {
        csvData.push({
          Load_Name: load.label || "Unnamed",
          Beam_ID: beamId,
          Type: load.type || "distributed_uniform",
          dx: load.dx ?? "",
          dy: load.dy ?? "",
          dz: load.dz ?? "",
          dxStart: load.dxLeft ?? "",
          dyStart: load.dyLeft ?? "",
          dzStart: load.dzLeft ?? "",
          dxEnd: load.dxRight ?? "",
          dyEnd: load.dyRight ?? "",
          dzEnd: load.dzRight ?? "",
          fx: load.fx ?? "",
          fy: load.fy ?? "",
          fz: load.fz ?? "",
          referenceNode: load.referenceNode ?? "",
          percentageFromNode: load.percentageFromNode ?? "",
          torque: load.torque ?? "",
          torqueDirection: load.torqueDirection ?? "",
        } as any);
      });
    }
  });

  const csv = Papa.unparse(csvData, { header: includeHeaders });
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Copy beam loads to clipboard
 */
export const copyBeamLoadsToClipboard = async (beamLoads: any[]): Promise<boolean> => {
  try {
    const copyData = beamLoads.flatMap((load: any) => {
      const beamIds = Array.isArray(load.beamIds) ? load.beamIds : [];
      if (beamIds.length === 0) {
        return [{
          Load_Name: load.label || "Unnamed",
          Beam_ID: "",
          Type: load.type || "distributed_uniform",
          dx: load.dx ?? "",
          dy: load.dy ?? "",
          dz: load.dz ?? "",
        }];
      }
      return beamIds.map((beamId: string) => ({
        Load_Name: load.label || "Unnamed",
        Beam_ID: beamId,
        Type: load.type || "distributed_uniform",
        dx: load.dx ?? "",
        dy: load.dy ?? "",
        dz: load.dz ?? "",
      }));
    });

    const csvString = Papa.unparse(copyData, { header: true });
    await navigator.clipboard.writeText(csvString);
    return true;
  } catch (error) {
    console.error("Failed to copy beam loads:", error);
    return false;
  }
};

// =====================================================
// Node Load CSV Operations
// =====================================================

export interface NodeLoadCSVData {
  Load_Name: string;
  Node_ID: string;
  Load_ID?: string;
  Type: string;
  rotationX: number;
  rotationY: number;
  rotationZ: number;
  perpendicularX: number;
  perpendicularY: number;
  perpendicularZ: number;
}

/**
 * Import node loads from CSV
 */
export const importNodeLoadsFromCSV = (
  file: File
): Promise<CSVImportResult<any>> => {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const presetMap = new Map<string, any>();
          const errors: string[] = [];
          const warnings: string[] = [];

          console.log("CSV Headers found:", results.meta?.fields);
          console.log("First row of CSV data:", results.data[0]);

          results.data.forEach((row: any, index: number) => {
            const rowNumber = index + 1;
            const loadName = row.LoadName || row.Load_Name || row.loadName || `NodeLoad_${rowNumber}`;
            const nodeId = row.NodeID || row.Node_ID || row.nodeId;
            const type = (row.type || row.Type || "regular").toLowerCase();

            console.log(`Row ${rowNumber}: loadName="${loadName}", nodeId="${nodeId}", type="${type}"`);

            if (type !== "regular" && type !== "perpendicular") {
              warnings.push(`Row ${rowNumber}: Invalid type "${type}", using "regular"`);
            }

            if (!presetMap.has(loadName)) {
              presetMap.set(loadName, {
                id: row.Load_ID || row.id?.toString() || Date.now().toString() + Math.random(),
                name: loadName,
                type: type === "perpendicular" ? "perpendicular" : "regular",
                nodeIds: [],
                rotationValues: {
                  X: parseFloat(row.rotationX) || 0,
                  Y: parseFloat(row.rotationY) || 0,
                  Z: parseFloat(row.rotationZ) || 0,
                },
                perpendicularValues: {
                  X: parseFloat(row.perpendicularX) || 0,
                  Y: parseFloat(row.perpendicularY) || 0,
                  Z: parseFloat(row.perpendicularZ) || 0,
                },
              });
            }

            const preset = presetMap.get(loadName);
            if (nodeId !== null && nodeId !== undefined && nodeId !== "") {
              preset.nodeIds.push(nodeId.toString());
              console.log(`Added nodeId "${nodeId}" to preset "${loadName}"`);
            } else {
              console.log(`No nodeId found for preset "${loadName}" in row ${rowNumber}. Raw nodeId value:`, nodeId);
            }
          });

          const csvString = Papa.unparse(results.data);

          resolve({
            success: errors.length === 0,
            data: Array.from(presetMap.values()),
            errors,
            warnings,
            preview: Array.from(presetMap.values()).slice(0, 5),
            csv: csvString,
          });
        } catch (error) {
          resolve({
            success: false,
            data: [],
            errors: [`Failed to process node loads: ${error}`],
            warnings: [],
            preview: [],
            csv: "",
          });
        }
      },
      error: (error) => {
        resolve({
          success: false,
          data: [],
          errors: [`CSV parsing failed: ${error.message}`],
          warnings: [],
          preview: [],
          csv: "",
        });
      },
    });
  });
};

/**
 * Export node loads to CSV
 */
export const exportNodeLoadsToCSV = (
  nodeLoads: any[],
  options: CSVExportOptions = {}
): void => {
  const { filename = generateFilename("node-loadcases"), includeHeaders = true } = options;

  const csvData: NodeLoadCSVData[] = [];

  nodeLoads.forEach((preset: any) => {
    const nodeIds = Array.isArray(preset.nodeIds) ? preset.nodeIds : [];

    if (nodeIds.length === 0) {
      csvData.push({
        Load_Name: preset.name || "Unnamed",
        Node_ID: "",
        Load_ID: preset.id,
        Type: preset.type || "regular",
        rotationX: preset.rotationValues?.X ?? 0,
        rotationY: preset.rotationValues?.Y ?? 0,
        rotationZ: preset.rotationValues?.Z ?? 0,
        perpendicularX: preset.perpendicularValues?.X ?? 0,
        perpendicularY: preset.perpendicularValues?.Y ?? 0,
        perpendicularZ: preset.perpendicularValues?.Z ?? 0,
      });
    } else {
      nodeIds.forEach((nodeId: string) => {
        csvData.push({
          Load_Name: preset.name || "Unnamed",
          Node_ID: nodeId,
          Load_ID: preset.id,
          Type: preset.type || "regular",
          rotationX: preset.rotationValues?.X ?? 0,
          rotationY: preset.rotationValues?.Y ?? 0,
          rotationZ: preset.rotationValues?.Z ?? 0,
          perpendicularX: preset.perpendicularValues?.X ?? 0,
          perpendicularY: preset.perpendicularValues?.Y ?? 0,
          perpendicularZ: preset.perpendicularValues?.Z ?? 0,
        });
      });
    }
  });

  const csv = Papa.unparse(csvData, { header: includeHeaders });
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Copy node loads to clipboard
 */
export const copyNodeLoadsToClipboard = async (nodeLoads: any[]): Promise<boolean> => {
  try {
    const copyData = nodeLoads.flatMap((preset: any) => {
      const nodeIds = Array.isArray(preset.nodeIds) ? preset.nodeIds : [];
      if (nodeIds.length === 0) {
        return [{
          Load_Name: preset.name || "Unnamed",
          Node_ID: "",
          Type: preset.type || "regular",
          rotationX: preset.rotationValues?.X ?? 0,
          rotationY: preset.rotationValues?.Y ?? 0,
          rotationZ: preset.rotationValues?.Z ?? 0,
          perpendicularX: preset.perpendicularValues?.X ?? 0,
          perpendicularY: preset.perpendicularValues?.Y ?? 0,
          perpendicularZ: preset.perpendicularValues?.Z ?? 0,
        }];
      }
      return nodeIds.map((nodeId: string) => ({
        Load_Name: preset.name || "Unnamed",
        Node_ID: nodeId,
        Type: preset.type || "regular",
        rotationX: preset.rotationValues?.X ?? 0,
        rotationY: preset.rotationValues?.Y ?? 0,
        rotationZ: preset.rotationValues?.Z ?? 0,
        perpendicularX: preset.perpendicularValues?.X ?? 0,
        perpendicularY: preset.perpendicularValues?.Y ?? 0,
        perpendicularZ: preset.perpendicularValues?.Z ?? 0,
      }));
    });

    const csvString = Papa.unparse(copyData, { header: true });
    await navigator.clipboard.writeText(csvString);
    return true;
  } catch (error) {
    console.error("Failed to copy node loads:", error);
    return false;
  }
};

/**
 * Detect load type from CSV headers
 */
export const detectLoadTypeFromHeaders = (headers: string[]): "beam" | "node" | "unknown" => {
  const headerSet = new Set(headers.map((h) => h.toLowerCase().trim()));

  const beamIndicators = [
    "load_name", "loadname", "beam_id", "beamid", "type",
    "dx", "dy", "dz", "dxstart", "dxend", "fx", "fy", "fz", "torque",
  ];

  const nodeIndicators = [
    "rotationx", "rotationy", "rotationz",
    "perpendicularx", "perpendiculary", "perpendicularz",
  ];

  const beamMatches = beamIndicators.filter((i) => headerSet.has(i)).length;
  const nodeMatches = nodeIndicators.filter((i) => headerSet.has(i)).length;

  if (beamMatches > nodeMatches && beamMatches >= 3) return "beam";
  if (nodeMatches > beamMatches && nodeMatches >= 3) return "node";
  return "unknown";
};